import React, { useEffect, useState } from 'react';
import {
  fetchAemPageContent,
  useEventListener,
  setConfigs,
  getBaseURL,
  azureSession,
  closeLoader,
  setErrorManagerCode,
  setErrorManagerEnabled,
  registerComponentsWrappers,
  hasValidToken,
  isPageAuthRequired,
} from '@msc-b2c/shared-utils';
import { AEMComponentRenderer } from '@msc-b2c/shared-components';
import { JwtSessionHandler } from '../../../../libs/mymsc-components/src/lib/jwtexpiredmodal/JwtExpiredModal';
import { TransformedNode } from '@msc-b2c/interfaces/interfaces';
import { wrappers } from '../../../../libs/mymsc-components/src/lib/wrappers';
import { useDispatch } from 'react-redux';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../azureB2CConfig';
import { BrowserRouter } from 'react-router-dom';
import { ErrorManagerCodeEnum } from '@msc-b2c/interfaces/enums';

export function App() {
  const [aemData, setAemData] = useState<TransformedNode>();
  const [baseURL] = useState(getBaseURL());

  const [azureMsalInstance, setAzureMsalInstance] = useState<PublicClientApplication | null>(null);

  const dispatch = useDispatch();

  useEventListener(window, 'author-content-update', () => {
    fetchAemPageContent(window.location.href).then((data) => {
      data && setAemData(data);
    });
  });

  useEffect(() => {
    fetchAemPageContent(window.location.href).then(async (data) => {
      data && setAemData(data);

      //TODO trigger this dispatch just one time
      dispatch(setConfigs(data?.properties?.configs));

      if (isPageAuthRequired() && !hasValidToken()) {
        dispatch(setErrorManagerCode(ErrorManagerCodeEnum.JWT_EXPIRED));
      }

      const { clientId, authorityBaseUrl, loginUserFlow, tenantName } =
        data?.properties?.configs?.azureB2CConfig ?? {};

      const azureB2CConf = msalConfig(
        clientId,
        authorityBaseUrl,
        '',
        '',
        loginUserFlow,
        tenantName
      );

      dispatch(setErrorManagerEnabled(true));

      const msalInstance = new PublicClientApplication(azureB2CConf);

      setAzureMsalInstance(msalInstance);
      await msalInstance.initialize();
      await msalInstance
        .handleRedirectPromise()
        .then((response) => {
          if (response) {
            console.log('Azure B2C response:', response);
            msalInstance.setActiveAccount(response.account);
            dispatch(
              azureSession({
                idToken: response.account.idToken,
                username: response.account.username,
                idTokenClaims: response.account.idTokenClaims,
                authority: response.authority,
              })
            );
          }
        })
        .catch((error) => {
          const errorMessage = error?.errorMessage || error?.message || '';
          closeLoader('.loader');

          if (errorMessage.includes(ErrorManagerCodeEnum.ACCOUNT_CREATION_FAILED)) {
            dispatch(setErrorManagerCode(ErrorManagerCodeEnum.ACCOUNT_CREATION_FAILED));
          } else if (errorMessage.includes(ErrorManagerCodeEnum.USER_CANCELED)) {
            // User canceled — handled via redirect elsewhere, no error modal needed
          } else if (errorMessage) {
            dispatch(setErrorManagerCode(ErrorManagerCodeEnum.GENERIC_LOGIN_ERROR));
          }
        });
      // Cleanup function
      return () => {
        // Optionally, abort pending fetch request or clear timeouts/intervals
      };
    });

    registerComponentsWrappers(wrappers);
  }, []);

  if (!azureMsalInstance) return <div style={{ minHeight: '100dvh' }} />;

  return (
    <MsalProvider instance={azureMsalInstance}>
      <BrowserRouter basename={`${baseURL}`}>
        <JwtSessionHandler />
        <AEMComponentRenderer
          aemDataRoot={aemData}
          wrappers={wrappers}
        />
      </BrowserRouter>
    </MsalProvider>
  );
}

export default App;
